/**
 * v1.6.1538 — billing notice and Pay Now (Asana 1218431462710845).
 *
 * The owner's billing lives in Chargebee; staff see the consequence, never the
 * invoices. Two things are new for EVERY screen in this panel:
 *
 *   1. any staff route can now answer a billing 403;
 *   2. restrictions can be STORE-SCOPED, so one unpaid store must not read as a
 *      whole-panel lock-out.
 */

export const BILLING_STATES = ['allowed', 'warning', 'restricted'] as const;
export type BillingState = typeof BILLING_STATES[number];

export const BILLING_REASONS = [
  'unpaid', 'paused', 'cancelled_for_non_payment', 'manual', 'rollup',
  // Asana 1220202714832247 contract — no billing account linked; nothing to pay.
  'no_billing_account',
] as const;

export const PERM_STATUS = 'billing_status.view';
export const PERM_PAY = 'billing_payment.create';

export interface BillingStatus {
  state: BillingState;
  reason: string | null;
  /** Display VERBATIM. Null when allowed. */
  message: string | null;
  restrict_at: string | null;
  amount_due_cents: number;
  currency_code: string | null;
  pay_now_available: boolean;
  storefront_down: boolean;
  decided_by_scope_type: string | null;
  /** OPAQUE. The platform's store-projection id, NOT the tenant's store number. */
  decided_by_scope_id: number | null;
  is_manual: boolean;
  grace_expires_at: string | null;
  /**
   * Asana 1220202714832247 contract — while a time-boxed manual block is
   * active, when it lifts itself ("locked until ..."). Display VERBATIM: dates
   * arrive pre-formatted for the tenant's country.
   */
  manual_block_ends_at: string | null;
}

export function blankStatus(): BillingStatus {
  return {
    state: 'allowed', reason: null, message: null, restrict_at: null,
    amount_due_cents: 0, currency_code: null, pay_now_available: false,
    storefront_down: false, decided_by_scope_type: null, decided_by_scope_id: null,
    is_manual: false, grace_expires_at: null, manual_block_ends_at: null,
  };
}

/**
 * Reads `GET /status`.
 *
 * Anything unrecognised degrades to ALLOWED — a panel that locks itself out
 * because it could not parse a response is worse than one that stays open while
 * the server keeps enforcing on every request anyway.
 */
export function readStatus(res: any): BillingStatus {
  const d = res?.data ?? res ?? {};
  const state = (BILLING_STATES as readonly string[]).includes(String(d.state))
    ? (d.state as BillingState) : 'allowed';
  const cents = Number(d.amount_due_cents);
  return {
    state,
    reason: d.reason ?? null,
    message: typeof d.message === 'string' && d.message.trim() ? d.message.trim() : null,
    restrict_at: d.restrict_at ?? null,
    amount_due_cents: Number.isFinite(cents) ? cents : 0,
    currency_code: d.currency_code ?? null,
    pay_now_available: d.pay_now_available === true,
    storefront_down: d.storefront_down === true,
    decided_by_scope_type: d.decided_by_scope_type ?? null,
    decided_by_scope_id: d.decided_by_scope_id != null ? Number(d.decided_by_scope_id) : null,
    is_manual: d.is_manual === true,
    grace_expires_at: d.grace_expires_at ?? null,
    manual_block_ends_at: d.manual_block_ends_at ?? null,
  };
}

export function isRestricted(s: BillingStatus): boolean { return s?.state === 'restricted'; }
export function isWarning(s: BillingStatus): boolean { return s?.state === 'warning'; }
/** True whenever there is anything to tell staff about. */
export function shouldShowNotice(s: BillingStatus): boolean {
  return s?.state === 'warning' || s?.state === 'restricted';
}

/** Only when the server says so — never inferred from the amount or the state. */
export function showPayNow(s: BillingStatus, perms: any): boolean {
  if (!s?.pay_now_available) return false;
  /**
   * Asana 1220202714832247 contract — a manual verdict is NOT lifted by
   * paying. Offering Pay Now beside "contact support" would send the owner to
   * spend money for nothing.
   */
  if (s?.is_manual === true) return false;
  try { return perms?.has?.(PERM_PAY) === true; } catch { return false; }
}

export function canViewStatus(perms: any): boolean {
  try { return perms?.has?.(PERM_STATUS) === true; } catch { return false; }
}

/**
 * THE DISTINCTION THE WHOLE INTERCEPTOR RESTS ON.
 *
 * A billing 403 and a permission 403 are the same status code. They are told
 * apart by the presence of `data.reason` — nothing else. Getting this wrong in
 * either direction is bad: treat a permission denial as a billing block and
 * every ordinary 403 becomes a full-screen lock-out; treat a billing block as a
 * permission denial and staff see "you don't have access" with no way to pay.
 */
export function isBillingForbidden(err: any): boolean {
  if (Number(err?.status) !== 403) return false;
  const reason = err?.error?.data?.reason;
  return typeof reason === 'string' && reason.length > 0;
}

/** The notice payload carried on a billing 403, which differs from /status. */
export function readForbidden(err: any): Partial<BillingStatus> & { scopeType: string | null } {
  const d = err?.error?.data ?? {};
  return {
    state: 'restricted',
    reason: d.reason ?? null,
    message: typeof err?.error?.message === 'string' ? err.error.message : null,
    restrict_at: d.restrict_at ?? null,
    is_manual: d.is_manual === true,
    pay_now_available: d.pay_now_available === true,
    scopeType: d.decided_by?.scope_type ?? null,
  };
}

// ── Copy ─────────────────────────────────────────────────────────────────────

export interface NoticeCopy { headline: string; supporting: string; action: string | null; }

/**
 * Money arrives in MINOR UNITS — `4900` is $49.00. Never rendered without the
 * division, and never divided twice.
 */
export function formatAmount(cents: number, currency: string | null): string {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return '';
  const amount = (n / 100).toFixed(2);
  return currency ? `${amount} ${currency}` : amount;
}

/**
 * v1.6.1570 — CORRECTED against the OpenAPI.
 *
 * v1.6.1538 treated `grace_expires_at` as a replacement restriction date. It is
 * not: it is when the GRACE EXCEPTION expires, and while one is active the
 * verdict is `allowed`. The two are different clocks on different things.
 *
 * So the deadline shown on a warning or a restriction is `restrict_at`, full
 * stop. Grace has its own notice — see `graceNote` — because a scope covered by
 * grace reads as `allowed` and would otherwise say nothing at all while a
 * balance is owed and a date is running out.
 */
export function effectiveDeadline(s: Partial<BillingStatus>): string | null {
  return s?.restrict_at ?? null;
}

/**
 * A balance IS owed, and a grace exception is covering it until this date.
 *
 * The state is `allowed`, so nothing else on this screen would mention it —
 * the owner finds out when grace lapses and the panel restricts. Only shown
 * when there is genuinely something owed.
 */
export function graceNote(s: Partial<BillingStatus>): string | null {
  /**
   * v1.6.1578 — a graced scope now reads WARNING, not allowed
   * (Asana 1218589622134986).
   *
   * The backend changed: "a grace period IS a warning". While a grace covers a
   * scope, directly or from an ancestor, the state is `warning` with
   * `restrict_at` = the grace expiry — switch on or off, owed or not.
   *
   * This guard was `state !== 'allowed'`, written against the old contract in
   * v1.6.1570. It would never have fired again. Kept for the allowed case
   * because an older backend still answers that way, but grace is now detected
   * from `grace_expires_at` alone.
   */
  const until = s?.grace_expires_at;
  if (!until) return null;
  const amount = formatAmount(s?.amount_due_cents ?? 0, s?.currency_code ?? null);
  return amount
    ? `A balance of ${amount} is owed. Access is covered until ${until} — pay before then to `
      + 'avoid losing access.'
    : `Access is covered by an exception until ${until}.`;
}

/**
 * Under an operator grace — the only reliable signal is `grace_expires_at`.
 *
 * `reason` is NULL under a grace when the auto-block switch is off or nothing
 * is owed, so it cannot be used to tell this apart.
 */
export function isGraced(s: Partial<BillingStatus>): boolean {
  return !!s?.grace_expires_at;
}

export function hasGrace(s: Partial<BillingStatus>): boolean { return graceNote(s) != null; }

export function noticeCopy(s: Partial<BillingStatus> & { scopeType?: string | null },
                           storeName?: string | null): NoticeCopy {
  const scope = s?.scopeType ?? s?.decided_by_scope_type ?? null;
  const deadline = effectiveDeadline(s);
  const amount = formatAmount(s?.amount_due_cents ?? 0, s?.currency_code ?? null);

  if (s?.reason === 'paused') {
    return { headline: 'Subscription paused',
      supporting: 'The billing subscription is paused; there is nothing to pay here.',
      action: 'Contact the account owner or support to have it resumed.' };
  }
  /**
   * Asana 1220202714832247 contract — `no_billing_account`: there is nothing to
   * pay (`pay_now_available` is false), so the only way forward is support.
   * Before this it fell through to "Pay now, or ask the account owner to pay."
   */
  if (s?.reason === 'no_billing_account') {
    return { headline: 'No billing account linked',
      supporting: 'There is nothing to pay here \u2014 no billing account is linked to this client yet.',
      action: 'Contact support to have one set up.' };
  }
  if (s?.reason === 'manual' || s?.is_manual === true) {
    // Paying does NOT clear a manual block, so Pay Now must not be the advice.
    /**
     * Asana 1220202714832247 contract — a SCHEDULED manual block reads
     * `warning` with `restrict_at` = when the lock begins; an active
     * time-boxed one carries `manual_block_ends_at`. Both shown verbatim.
     */
    const scheduled = s?.state === 'warning' && !!s?.restrict_at;
    const supporting = scheduled
      ? `Access will be restricted on ${s?.restrict_at}.`
      : (s?.manual_block_ends_at
          ? `Access is restricted until ${s?.manual_block_ends_at}.`
          : (s?.message ?? ''));
    return { headline: scheduled ? 'Restriction scheduled by the platform team'
                                 : 'Restricted by the platform team',
      supporting,
      action: 'Contact support \u2014 paying will not lift this.' };
  }
  /**
   * v1.6.1578 — a GRACE reads as a warning now, and must not claim an invoice.
   *
   * A grace can cover an account with nothing owed at all, and with the
   * auto-block switch off `reason` comes back NULL. The old warning copy said
   * "the account owner has an unpaid invoice" unconditionally — false on a
   * clean account, and the exact wording the backend went out of its way to
   * replace with its own grace message.
   *
   * The server's `message` is shown beneath this either way; what changes here
   * is that the panel stops asserting a cause it does not know.
   */
  if (isGraced(s)) {
    const until = s?.grace_expires_at;
    return { headline: 'Payment grace period',
      supporting: amount
        ? `A balance of ${amount} is owed. Access continues until ${until}.`
        : `An operator has granted a grace period. Access continues until ${until}.`,
      action: amount ? 'Settle the balance before it ends to keep access.' : null };
  }
  if (s?.state === 'warning') {
    return { headline: 'Billing warning',
      supporting: deadline
        ? (amount
            ? `The account owner's balance of ${amount} is unpaid. This panel keeps working until ${deadline}.`
            : `This panel keeps working until ${deadline}.`)
        : 'The account owner has an unpaid balance.',
      action: 'Pay now, or ask the account owner to pay.' };
  }
  /**
   * v1.6.1570 — `account` is a real scope value and the one that ROLLS DOWN.
   *
   * v1.6.1538 only handled `store` and `tenant`, so an account-level
   * restriction — the case where the whole client is blocked, not this tenant —
   * fell through to the generic unpaid wording and looked like a local problem
   * the operator could fix.
   */
  if (scope === 'account') {
    return { headline: 'This account is restricted',
      supporting: s?.message ?? 'The restriction is on the whole billing account, not just this store.',
      action: 'Pay now, or ask the account owner to pay.' };
  }
  if (scope === 'store') {
    return { headline: 'This store is restricted',
      supporting: storeName
        ? `Only ${storeName} is affected; your other stores keep working.`
        : 'Only this store is affected; your other stores keep working.',
      action: 'Pay now, or ask the account owner to pay.' };
  }
  if (s?.reason === 'rollup') {
    return { headline: 'This panel is restricted',
      supporting: 'Every store of this tenant is restricted.',
      action: 'Pay now, or ask the account owner to pay.' };
  }
  return { headline: 'This panel is restricted',
    supporting: amount
      ? `The account owner's balance of ${amount} is unpaid.`
      : 'The account owner has an unpaid balance.',
    action: 'Pay now, or ask the account owner to pay.' };
}

/** Appended when the public storefront is down too — staff will be asked. */
export const STOREFRONT_DOWN_LINE = 'The public store is offline for customers.';

export const PAYMENT_IN_PROGRESS: NoticeCopy = {
  headline: 'Payment in progress',
  supporting: 'Access is restored within a few minutes after the payment succeeds \u2014 '
            + 'this takes a minute or two.',
  action: null,
};

// ── Polling ──────────────────────────────────────────────────────────────────

export const POLL_INTERVAL_MS = 12_000;
export const POLL_MAX_MS = 5 * 60_000;

/**
 * Restoration is never instant — typically one to three minutes. Polling stops
 * at five so a forgotten tab does not hammer the endpoint forever.
 */
export function shouldKeepPolling(startedAt: number, now: number): boolean {
  return now - startedAt < POLL_MAX_MS;
}

/**
 * A PARTIAL payment does not restore access. The notice stays until the balance
 * is actually zero, so "state is no longer restricted" is not sufficient on its
 * own while an amount is still outstanding.
 */
export function isSettled(s: BillingStatus): boolean {
  return s?.state === 'allowed' && Number(s?.amount_due_cents ?? 0) === 0;
}

// ── Pay Now errors ───────────────────────────────────────────────────────────

/** 429 carries the wait in its message; the button is disabled for that long. */
export function retryAfterSeconds(err: any): number | null {
  if (Number(err?.status) !== 429) return null;
  const m = String(err?.error?.message ?? '').match(/(\d+)\s*second/i);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 60;
}

/**
 * A 403 with NO `data.reason` on /pay-now is a missing permission, not a
 * billing block: hide the button, keep the notice, and say who can pay.
 */
export function isPermissionDenied(err: any): boolean {
  return Number(err?.status) === 403 && !isBillingForbidden(err);
}

export const NO_PAY_PERMISSION =
  'You don\u2019t have permission to open the payment page. Ask the account owner to pay.';

/**
 * v1.6.1570 — Chargebee answered without a usable page.
 *
 * Explicitly SAFE TO RETRY per the contract, and nothing was changed on the
 * platform — so this must not read like a refusal. `meta.chargebee_error_code`
 * is carried when Chargebee returned one.
 */
export function isChargebeeOutage(err: any): boolean { return Number(err?.status) === 502; }

export function chargebeeErrorCode(err: any): string | null {
  const c = err?.error?.meta?.chargebee_error_code;
  return typeof c === 'string' && c.trim() ? c.trim() : null;
}

export const CHARGEBEE_RETRY_NOTE = 'Nothing was charged. Try again in a minute.';

/** Chargebee's own expiry for the hosted page, when it reported one. */
export function payNowExpiry(res: any): string | null {
  const v = (res?.data ?? res ?? {})?.expires_at;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export function payNowUrl(res: any): string | null {
  const u = (res?.data ?? res ?? {})?.url;
  return typeof u === 'string' && /^https?:\/\//i.test(u) ? u : null;
}

/** Server messages here are written for the reader; shown verbatim. */
export function apiMessage(err: any, fallback: string): string {
  return String(err?.error?.message ?? '').trim() || fallback;
}
